/**
 * Pure derivations for the Rooms tab summary bar and export/print features.
 * Consumes data already loaded in AdminDashboard: rooms, room assignments
 * (with a registrations join) and the camp's non-cancelled registrations.
 */

export const ROOM_ELIGIBLE_STATUSES = ['Confirmed', 'Paid']

export function getAge(dob) {
  if (!dob) return null
  const today = new Date()
  const birth = new Date(dob + 'T00:00:00')
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

function byName(a, b) {
  return String(a.name).localeCompare(String(b.name))
}

export function buildRoomsReportData(rooms = [], roomAssignments = [], roomCampRegs = []) {
  // roomCampRegs already excludes Cancelled; anyone assigned but missing here is cancelled.
  const activeRegIds = new Set(roomCampRegs.map(r => r.id))
  const assignedRegIds = new Set(roomAssignments.map(a => a.registration_id))

  const eligibleRegs = roomCampRegs.filter(r => ROOM_ELIGIBLE_STATUSES.includes(r.payment_status))
  const assignedEligible = eligibleRegs.filter(r => assignedRegIds.has(r.id))
  const pendingEligible = eligibleRegs.filter(r => !assignedRegIds.has(r.id))

  const roomsList = [...rooms]
    .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
    .map(room => {
      const occupants = roomAssignments
        .filter(a => a.room_id === room.id)
        .map(a => {
          const reg = a.registrations ?? {}
          return {
            name: reg.full_name ?? '',
            sex: reg.sex ?? '',
            age: getAge(reg.date_of_birth),
            cancelled: !activeRegIds.has(a.registration_id),
          }
        })
        .sort(byName)
      return {
        id: room.id,
        name: room.name ?? '',
        capacity: room.capacity ?? 0,
        sex: room.sex ?? '',
        minAge: room.min_age ?? null,
        maxAge: room.max_age ?? null,
        occupantCount: occupants.length,
        occupants,
      }
    })

  const pending = pendingEligible
    .map(r => ({ name: r.full_name ?? '', sex: r.sex ?? '', age: getAge(r.date_of_birth) }))
    .sort(byName)

  return {
    summary: {
      assigned: assignedEligible.length,
      pending: pendingEligible.length,
      pendingMale: pendingEligible.filter(r => r.sex === 'Male').length,
      pendingFemale: pendingEligible.filter(r => r.sex === 'Female').length,
      totalEligible: eligibleRegs.length,
    },
    rooms: roomsList,
    pending,
  }
}

const CSV_HEADER = [
  'Room Name', 'Assigned', 'Capacity', 'Room Sex',
  'Min Age', 'Max Age', 'Person Name', 'Person Age', 'Person Sex',
]

function csvField(value) {
  const s = value == null ? '' : String(value)
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s
}

export function roomsToCsv(data) {
  const rows = [CSV_HEADER]

  data.rooms.forEach(room => {
    const roomCols = [
      room.name,
      room.occupantCount,
      room.capacity,
      room.sex,
      room.minAge ?? '',
      room.maxAge ?? '',
    ]
    if (room.occupants.length === 0) {
      rows.push([...roomCols, '', '', ''])
    } else {
      room.occupants.forEach(o => {
        const name = o.cancelled ? `${o.name} (cancelled)` : o.name
        rows.push([...roomCols, name, o.age ?? '', o.sex])
      })
    }
  })

  data.pending.forEach(p => {
    rows.push(['PENDING — not assigned', '', '', '', '', '', p.name, p.age ?? '', p.sex])
  })

  return rows.map(r => r.map(csvField).join(',')).join('\n')
}
